// Enhanced NotesTreeList.js
import React, { useState, useMemo, useEffect } from 'react';
import { FolderOpen, FileText, Code, Image, Star, Clock, ChevronRight, Plus, MoreHorizontal } from 'lucide-react';
import styles from './NotesTreeList.module.css';
import { useCategories } from '../contexts/CategoriesContext';
import { useNotes } from '../contexts/NotesContext';
import { formatDistanceToNow } from 'date-fns';






const NoteIcon = ({ note, hasSubnotes, isExpanded, toggleExpand }) => {
  // If this is a parent note with subnotes, make the entire icon clickable for expansion
  if (hasSubnotes) {
    return (
      <div className={styles.folderIconContainer}>
        <button 
          className={styles.folderButton}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpand();
          }}
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          <FolderOpen className={styles.folderIcon} />
          <ChevronRight 
            className={`${styles.expandArrow} ${isExpanded ? styles.expanded : ''}`} 
            size={10} 
          />
        </button>
      </div>
    );
  }
  
  // For regular notes (no subnotes), return appropriate icon
  const hasCode = note.note && note.note.includes('class="ql-syntax"');
  const hasImages = note.images && note.images.length > 0;
  
  
  if (hasCode) {
    return <Code className={styles.codeIcon} />;
  } else if (hasImages) {
    return <Image className={styles.imageIcon} />;
  } else {
    return <FileText className={styles.fileIcon} />;
  }
};

const TimeIndicator = ({ date }) => {
  if (!date) return null;
  
  try {
    const noteDate = new Date(date);
    const timeAgo = formatDistanceToNow(noteDate, { addSuffix: true });
    
    // Check if note is recent (less than 24 hours)
    const isRecent = (Date.now() - noteDate.getTime()) < 86400000;
    
    return (
      <span className={`${styles.timeIndicator} ${isRecent ? styles.recent : ''}`}>
        <Clock className={styles.timeIcon} />
        <span className={styles.timeText}>{timeAgo}</span>
      </span>
    );
  } catch (e) {
    return null;
  }
};
const NotesTreeList = ({ onNoteSelect, selectedNoteId, visibleCategoryIds = null, expandedCategories, setExpandedCategories, sortOrder = 'newest', folderSort = 'none' }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    type: null,
    itemId: null
  });

  const { categories, removeCategory } = useCategories();
  const { notes, subnotes, createSubnote } = useNotes();


  // Group notes by category and filter out subnotes from main list
const notesByCategory = useMemo(() => {
  if (!categories || !notes) return [];

  const topLevelNotes = notes.filter(note => !note.parent_id);
  const notesMap = new Map();

  topLevelNotes.forEach(note => {
    const categoryId = note.primary_category_id || 1;
    if (!notesMap.has(categoryId)) {
      notesMap.set(categoryId, []);
    }
    notesMap.get(categoryId).push(note);
  });

  let filteredCategories = categories;
  if (visibleCategoryIds && visibleCategoryIds.length > 0) {
    filteredCategories = categories.filter(cat => visibleCategoryIds.includes(cat.id));
  }

return filteredCategories.map(category => ({
  ...category,
  notes: (notesMap.get(category.id) || []).sort((a, b) => {
    const aHasSubnotes = (subnotes[a.id] || []).length > 0;
    const bHasSubnotes = (subnotes[b.id] || []).length > 0;
    
    // First sort by folder preference if set
    if (folderSort === 'top') {
      if (aHasSubnotes && !bHasSubnotes) return -1;
      if (!aHasSubnotes && bHasSubnotes) return 1;
    } else if (folderSort === 'bottom') {
      if (aHasSubnotes && !bHasSubnotes) return 1;
      if (!aHasSubnotes && bHasSubnotes) return -1;
    }
    
    // Then apply regular sorting
    if (sortOrder === 'newest') {
      return new Date(b.date) - new Date(a.date);
    } else if (sortOrder === 'oldest') {
      return new Date(a.date) - new Date(b.date);
    } else if (sortOrder === 'title') {
      return (a.title || 'Untitled').localeCompare(b.title || 'Untitled');
    }
    return 0;
  })
}));
}, [categories, notes, visibleCategoryIds, sortOrder, folderSort, subnotes]);




  // Track expanded notes (for subnotes)
  const [expandedNotes, setExpandedNotes] = useState(() => {
    const saved = localStorage.getItem('expandedNotes');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const toggleExpandNote = (noteId) => {
    const newExpandedNotes = new Set(expandedNotes);
    if (expandedNotes.has(noteId)) {
      newExpandedNotes.delete(noteId);
    } else {
      newExpandedNotes.add(noteId);
    }
    setExpandedNotes(newExpandedNotes);
    localStorage.setItem('expandedNotes', JSON.stringify([...newExpandedNotes]));
  };


  const handleContextMenuClick = async (action, type, itemId) => {
    setContextMenu({ show: false });
    
    if (type === 'category') {
      // Handle category context menu
      const category = notesByCategory.find(cat => cat.id === itemId);
      if (category) {
        switch(action) {
          case 'delete':
            if (window.confirm(`Are you sure you want to delete "${category.name}" and reassign its notes?`)) {
              try {
                await removeCategory(itemId);
              } catch (error) {
                console.error('Error:', error);
                alert('Failed to delete category. Please try again.');
              }
            }
            break;
            
          case 'rename':
            // Future rename functionality
            break;
        }
      }
    } else if (type === 'note') {
      // Handle note context menu
      switch(action) {
        case 'add_subnote':
          await createSubnote(itemId);
          // Expand the parent note to show the new subnote
          const newExpandedNotes = new Set(expandedNotes);
          newExpandedNotes.add(itemId);
          setExpandedNotes(newExpandedNotes);
          localStorage.setItem('expandedNotes', JSON.stringify([...newExpandedNotes]));
          break;
        
            
        case 'delete':
          // Delete functionality is handled elsewhere
          break;
          
        case 'move':
          // Future move functionality
          break;
      }
    }
  };

  const handleContextMenu = (e, type, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // For clicks on the "..." button - identify them by checking the target or its parents
    const isMoreButton = e.target.closest(`.${styles.actionButton}`);
    
    // Get left panel width (or use a fixed value if you know it)
    const leftPanelWidth = 260; // Adjust to match your actual panel width
    
    // Calculate x position - keep the menu inside the left panel
    let xPos;
    if (isMoreButton) {
      // If clicked on the "..." button, position menu to the left of the button
      xPos = Math.max(10, e.pageX - 150); // Menu width is ~150px
    } else {
      // For regular right-clicks, position at cursor but keep inside panel
      xPos = Math.min(e.pageX, leftPanelWidth - 150); // Keep the menu inside the panel
    }
    
    setContextMenu({
      show: true,
      x: xPos,
      y: e.pageY,
      type,
      itemId
    });
  };

  // Function to close context menu when clicking elsewhere
  const handleGlobalClick = () => {
    if (contextMenu.show) {
      setContextMenu({ ...contextMenu, show: false });
    }
  };

  // Add global click handler
  React.useEffect(() => {
    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [contextMenu.show]);

  // Render a note item (main note or subnote)
const renderNoteItem = (note, isSubnote = false) => {
  const noteSubnotes = subnotes[note.id] || [];
  const hasSubnotes = noteSubnotes.length > 0;
  
  // Determine if the note is recent (less than 24 hours old)
  const isRecent = note.date && (Date.now() - new Date(note.date).getTime()) < 86400000;

  return (
    <div key={`note-${note.id}`} className={styles.noteContainer}>
      <div
        className={`${styles.treeNoteItem} ${isSubnote ? styles.subnoteItem : ''} ${selectedNoteId === note.id ? styles.selected : ''}`}
        onClick={() => onNoteSelect(note.id)}
        onContextMenu={(e) => handleContextMenu(e, 'note', note.id)}
      >
        <div className={styles.noteIconContainer}>
          {hasSubnotes ? (
            <button 
              className={`${styles.expandButton} ${expandedNotes.has(note.id) ? styles.expanded : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpandNote(note.id);
              }}
              aria-label={expandedNotes.has(note.id) ? "Collapse" : "Expand"}
            >
              <FolderOpen className={styles.folderIcon} />
              <ChevronRight size={12} className={`${styles.chevronIcon} ${expandedNotes.has(note.id) ? styles.expanded : ''}`} />
            </button>
          ) : (
            <NoteIcon note={note} hasSubnotes={false} />
          )}
        </div>
        
        <div className={styles.noteContent}>
          {note.stickyCount > 0 && <span className={styles.stickyCount}>({note.stickyCount}) - </span>}
          <span className={styles.noteTitle}>{note.title || 'Untitled'}</span>
          <div className={styles.noteIndicators}>
            {isRecent && <span className={styles.recentBadge}>New</span>}
          </div>
        </div>
        
        <div className={styles.noteActions}>
          {!isSubnote && (
            <button 
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                handleContextMenuClick('add_subnote', 'note', note.id);
              }}
              title="Add subnote"
            >
              <Plus size={14} />
            </button>
          )}
          
          <button 
            className={styles.actionButton}
            onClick={(e) => {
              e.stopPropagation();
              handleContextMenu(e, 'note', note.id);
            }}
            title="More options"
          >
            <MoreHorizontal size={14} />
          </button>
        </div>
      </div>
      
      {/* Render subnotes if this note has any and is expanded */}
      {hasSubnotes && expandedNotes.has(note.id) && (
        <div className={styles.subnotesList}>
          {noteSubnotes.map(subnote => renderNoteItem(subnote, true))}
        </div>
      )}
    </div>
  );
};

  return (
    <div className={styles.notebooksTree}>
      {notesByCategory.map((category) => (
        <div
          key={category.id}
          className={styles.categoryBlock}
        >
          <div
            className={`${styles.categoryHeader} ${expandedCategories.has(category.id) ? styles.expanded : ''}`}
            onClick={(e) => {
              e.preventDefault();
              handleCategorySelect(category.id);
              const newExpandedCategories = new Set(expandedCategories);
              if (expandedCategories.has(category.id)) {
                newExpandedCategories.delete(category.id);
              } else {
                newExpandedCategories.add(category.id);
              }
              setExpandedCategories(newExpandedCategories);
              localStorage.setItem('expandedCategories', JSON.stringify([...newExpandedCategories]));
            }}
            onContextMenu={(e) => handleContextMenu(e, 'category', category.id)}
          >
            <div className={styles.categoryToggle}>
              <ChevronRight 
                className={expandedCategories.has(category.id) ? styles.expanded : ''} 
                size={16}
              />
            </div>
            
            <div className={styles.categoryInfo}>
              <span className={styles.categoryName}>
                {category.name}
              </span>
              <span className={styles.noteCount}>
                {category.notes.length}
              </span>
            </div>
          </div>
          
          {expandedCategories.has(category.id) && (
            <div className={styles.categoryContent}>
              {category.notes.length > 0 ? (
                category.notes.map(note => renderNoteItem(note))
              ) : (
                <div className={styles.emptyCategory}>No notes in this category</div>
              )}
            </div>
          )}
        </div>
      ))}

      {contextMenu.show && (
        <div 
          className={styles.contextMenu}
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
          }}
        >
          {contextMenu.type === 'note' && (
            <>
              {/* Only show Add Subnote for top-level notes */}
              {!Object.values(subnotes).flat().some(n => n.id === contextMenu.itemId) && (
                <div 
                  className={styles.contextMenuItem}
                  onClick={() => handleContextMenuClick('add_subnote', contextMenu.type, contextMenu.itemId)}
                >
                  <Plus size={14} className={styles.menuItemIcon} />
                  Add Subnote
                </div>
              )}

              <div 
                className={styles.contextMenuItem}
                onClick={() => handleContextMenuClick('rename', contextMenu.type, contextMenu.itemId)}
              >
                <span className={styles.menuItemIcon}>✏️</span>
                Rename
              </div>
              <div 
                className={`${styles.contextMenuItem} ${styles.dangerItem}`}
                onClick={() => handleContextMenuClick('delete', contextMenu.type, contextMenu.itemId)}
              >
                <span className={styles.menuItemIcon}>🗑️</span>
                Delete
              </div>
            </>
          )}
          
          {contextMenu.type === 'category' && (
            <>
              <div 
                className={styles.contextMenuItem}
                onClick={() => handleContextMenuClick('rename', contextMenu.type, contextMenu.itemId)}
              >
                <span className={styles.menuItemIcon}>✏️</span>
                Rename
              </div>
              <div 
                className={`${styles.contextMenuItem} ${styles.dangerItem}`}
                onClick={() => handleContextMenuClick('delete', contextMenu.type, contextMenu.itemId)}
              >
                <span className={styles.menuItemIcon}>🗑️</span>
                Delete
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(NotesTreeList);